import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
} from "@/api/notifications";
import { API, graphqlOperation } from "aws-amplify";
import * as overrides from "@/graphql/override_queries";

const state = {
  notifications: [],
  unreadCount: 0,
  nextToken: null,
  isLoading: false,
  subscription: null,
  // The bell's menu; the dashboard's "See all" opens it.
  bellOpen: false,
};

const mutations = {
  SET_NOTIFICATIONS(state, { items, nextToken }) {
    state.notifications = items;
    state.nextToken = nextToken;
  },
  APPEND_NOTIFICATIONS(state, { items, nextToken }) {
    state.notifications = [...state.notifications, ...items];
    state.nextToken = nextToken;
  },
  PREPEND_NOTIFICATION(state, notification) {
    state.notifications = [notification, ...state.notifications];
  },
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count;
  },
  MARK_READ(state, notificationId) {
    const notification = state.notifications.find(
      (n) => n.id === notificationId
    );
    if (notification) {
      notification.isRead = "true";
    }
    if (state.unreadCount > 0) {
      state.unreadCount -= 1;
    }
  },
  MARK_ALL_READ(state) {
    state.notifications.forEach((n) => {
      n.isRead = "true";
    });
    state.unreadCount = 0;
  },
  SET_LOADING(state, loading) {
    state.isLoading = loading;
  },
  SET_BELL_OPEN(state, open) {
    state.bellOpen = !!open;
  },
  SET_SUBSCRIPTION(state, subscription) {
    state.subscription = subscription;
  },
  CLEAR_STATE(state) {
    state.notifications = [];
    state.unreadCount = 0;
    state.nextToken = null;
    state.isLoading = false;
    if (state.subscription) {
      state.subscription.unsubscribe();
      state.subscription = null;
    }
  },
};

const actions = {
  async loadNotifications({ commit, rootState }, options = {}) {
    const owner = rootState.user?.username;
    if (!owner) return;

    commit("SET_LOADING", true);
    try {
      const result = await fetchNotifications(owner, {
        limit: options.limit || 20,
        nextToken: options.nextToken || null,
      });

      const items = result.data?.items || [];
      const nextToken = result.data?.nextToken || null;

      if (options.nextToken) {
        commit("APPEND_NOTIFICATIONS", { items, nextToken });
      } else {
        commit("SET_NOTIFICATIONS", { items, nextToken });
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async loadUnreadCount({ commit, rootState }) {
    const owner = rootState.user?.username;
    if (!owner) return;

    try {
      const count = await fetchUnreadCount(owner);
      commit("SET_UNREAD_COUNT", count);
    } catch (error) {
      console.error("Failed to load unread count:", error);
    }
  },

  async markRead({ commit }, notificationId) {
    // Optimistic update
    commit("MARK_READ", notificationId);

    try {
      await markAsRead(notificationId);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  },

  async markAllRead({ commit, rootState }) {
    const owner = rootState.user?.username;
    if (!owner) return;

    // Optimistic update for visible notifications
    commit("MARK_ALL_READ");

    try {
      // Fetch ALL unread notification IDs, not just the loaded page
      const allUnreadIds = [];
      let nextToken = null;

      do {
        const input = {
          owner,
          isRead: { eq: "false" },
          limit: 100,
        };
        if (nextToken) input.nextToken = nextToken;

        const res = await API.graphql(
          graphqlOperation(overrides.notificationsByOwnerReadStatus, input)
        );

        const data = res.data.notificationsByOwnerReadStatus;
        const ids = (data.items || []).map((n) => n.id);
        allUnreadIds.push(...ids);
        nextToken = data.nextToken;
      } while (nextToken);

      if (allUnreadIds.length > 0) {
        await markAllAsRead(allUnreadIds);
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  },

  subscribeToNotifications({ commit, rootState }) {
    const owner = rootState.user?.username;
    if (!owner) return;

    try {
      const subscription = API.graphql(
        graphqlOperation(overrides.onCreateNotification, { owner })
      ).subscribe({
        next: ({ value }) => {
          const notification = value.data.onCreateNotification;
          if (notification) {
            commit("PREPEND_NOTIFICATION", notification);
            if (notification.isRead === "false") {
              commit("SET_UNREAD_COUNT", state.unreadCount + 1);
            }
          }
        },
        error: (error) => {
          console.error("Notification subscription error:", error);
        },
      });

      commit("SET_SUBSCRIPTION", subscription);
    } catch (error) {
      console.error("Failed to subscribe to notifications:", error);
    }
  },

  unsubscribe({ commit, state }) {
    if (state.subscription) {
      state.subscription.unsubscribe();
    }
    commit("CLEAR_STATE");
  },
};

const getters = {
  allNotifications: (state) => state.notifications,
  unreadCount: (state) => state.unreadCount,
  hasUnread: (state) => state.unreadCount > 0,
  recentNotifications: (state) => state.notifications.slice(0, 10),
  isLoading: (state) => state.isLoading,
  hasMore: (state) => state.nextToken !== null,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
